/** The panels behind a filing's tabs that are not already their own file: Disclosures, Checklist
 * and Findings.
 *
 * Disclosures is the lines exactly as they were drawn on the long page (`ReportsLines`) and the
 * notices that qualify their total (`ReportsQualifiers`). Checklist is the compliance checklist,
 * the unexplained differences that hold a filing (CTX-04) and the rules-package test. Findings is
 * the list of rules the report did not pass.
 *
 * **The long explanations are one collapsed *Basis and limits* each, never deleted** (33-ui-build-spec,
 * rank 3): the wording is the original's, and a test reads it with the panel closed.
 */

import { Link } from "@tanstack/react-router";

import type { FilingCheck, FilingChecks } from "../api/filingChecks";
import type { Finding, Obligation, Report, Scope2Method } from "../api/queries";
import { ReadFailed } from "../components/Loaded";
import { Empty, Panel } from "../components/Panel";
import { WindowLockBanner } from "../components/WindowLockBanner";
import { shortDate } from "../lib/format";
import { ComplianceChecklistPanel } from "./ComplianceChecklist";
import { PackagePreflightPanel } from "./PackagePreflight";
import { FindingRow } from "./ReportsFindingRow";
import { FiguresInRegister, Line } from "./ReportsLines";
import { Qualifiers } from "./ReportsQualifiers";
import { Unreconciled } from "./ReportsUnreconciled";
import own from "./Reports.module.css";
import { countsLine, failingChecks } from "./filingChecksModel";
import { amount, cls, receiptIdsOf } from "./reportsFormat";
import { hasQualifiers } from "./reportsModel";

export function DisclosuresTab({
  report,
  active,
  method,
  role,
}: {
  report: Report;
  active: Obligation;
  method: Scope2Method;
  role: string | undefined;
}) {
  return (
    <>
      <Panel
        title={report.regime_name}
        sub={`${report.context.legal_entity_name} · ${shortDate(report.context.period_start)} – ${shortDate(report.context.period_end)} · ${report.scope2_method}-based`}
        column="1 / 13"
      >
        <div data-testid="report-lines">
          {/* Whether the window this report totals is closed (ADR-0094 D2). From the report's
              own response, so it cannot disagree with the figures beneath it. */}
          <WindowLockBanner lock={report.window_lock} />
          {/* **What priced these figures, stated rather than assumed** (M-01, SECR 20F via
              U-20). Neither regime mandates a GWP set — 20F requires the company to state
              the methodologies it *used*, and HSC §38532(c)(2)(A)(ii) mandates the GHG
              Protocol and names no set — so which one priced a tonne is a disclosure, and
              nothing on this screen said it.

              Beside the lines rather than inside one: the methodologies line is NARRATIVE,
              words the customer writes, and this is the part the product knows. Publisher
              and edition travel with the label because "AR5" alone is not checkable.

              **More than one row is legitimate.** A UK group with a Californian subsidiary
              is priced by DESNZ and by EPA. Shown, never refused — rule 2 gives us no
              paragraph forbidding a mixed basis. */}
          {report.gwp_basis.length > 0 ? (
            <p className={own.gwpBasis} data-testid="gwp-basis">
              <span className={own.gwpBasisLabel}>Priced under</span>
              {report.gwp_basis.map((basis, i) => (
                <span key={`${basis.label}-${basis.publisher}-${basis.edition}`}>
                  {i > 0 ? " · " : " "}
                  {/* A null label is an absence, never a default. Saying "not recorded" is
                      the honest render; printing "AR5" because every other row says AR5 is
                      the gap-fill that C-15 exists to refuse. */}
                  {basis.label ?? "not recorded"}
                  {basis.publisher ? `, ${basis.publisher}` : ""}
                  {basis.edition ? ` ${basis.edition}` : ""}
                  <span className={own.gwpBasisCount}>
                    {" "}
                    ({basis.records} {basis.records === 1 ? "figure" : "figures"})
                  </span>
                </span>
              ))}
            </p>
          ) : null}
          {report.lines.map((line) => (
            <Line
              key={line.key}
              line={line}
              regimeKey={active.regime_key}
              obligationId={active.obligation_id}
              method={method}
              filingWindow={{
                period_start: report.context.period_start,
                period_end: report.context.period_end,
              }}
              entityName={report.context.legal_entity_name}
            />
          ))}
          {/* The total the **regulation** asks for. The guidance line is printed above it and
              left out of it: folding it in would be this product settling B-19 by
              arithmetic. */}
          <div className={cls(own.total)}>
            <span>Total stated under the regulation</span>
            <span data-testid="report-stated-total">
              {amount(report.stated_total_tonnes, "tCO2e")} tCO₂e
            </span>
          </div>
        </div>
      </Panel>

      {hasQualifiers(report, role) ? (
        <Panel
          title="What qualifies this total"
          sub="Read these before relying on the figure"
          column="1 / 13"
        >
          <Qualifiers report={report} role={role} />
        </Panel>
      ) : null}

      <details className={own.basisBox} data-testid="disclosures-basis">
        <summary>Basis and limits</summary>
        {/* The fine print: what a filed report is, where the receipts are, whose verification
            this is. Demoted to small print and kept whole (33-ui-build-spec, rank 3). */}
        <div className={cls(own.finePrint)}>
          <p className={cls(own.fine)}>
            A filed report is an <strong>artifact</strong>: hashed, pinned to a ledger state, and
            reproducible byte for byte from the same data. A renderer that is not deterministic
            produces a different artifact from identical data, which makes the artifact worthless
            as evidence. So the record is the canonical JSON, and the HTML and PDF are views of
            it. The PDF is drawn on request from the stored record and is not itself stored or
            hashed.
          </p>
          <p className={cls(own.fine)} data-testid="report-receipts-note">
            Every figure in the disclosures is a sum of the same calculations the rest of the
            product reads, and each of those has a receipt on <strong>Emissions</strong>. That part
            is not a preview.{" "}
            {report.lines.some((line) => receiptIdsOf(line).ids.length > 0) ? (
              <>
                A line that lists its calculations opens each one&rsquo;s receipt from the line
                itself. <strong>A line that lists none does not open receipts itself</strong>: it
                is reached through the register, and an apportioned line also opens the records it
                cuts.
              </>
            ) : (
              <>
                <strong>A line does not open its receipts itself</strong>: the report counts the
                calculations behind a line but does not list which they are, so the records an
                apportioned line cuts open directly and everything else is reached through the
                register.
              </>
            )}{" "}
            <FiguresInRegister report={report} testId="report-figures-in-register">
              Show every figure for {report.context.legal_entity_name} in this period
            </FiguresInRegister>
          </p>

          {active.in_force_caveat ? (
            <p className={cls(own.fine)}>
              <strong>{active.in_force_label}.</strong> {active.in_force_caveat}
            </p>
          ) : null}

          <p className={cls(own.fine)}>
            <strong>Verification is not human-reviewed.</strong> Every paragraph quoted in the
            disclosures traces to a primary source fetched and quoted by Claude. No person has
            independently opened those sources. For an assurance engagement that is a different
            control, and only one of the two is true.
          </p>
        </div>
      </details>
    </>
  );
}

export function ChecklistTab({
  report,
  active,
  method,
}: {
  method: Scope2Method;
  /** `undefined` while the report is being read or after a failed read: the checklist then says
   * *Not checked* per section, which is its own rule. */
  report: Report | undefined;
  active: Obligation;
}) {
  return (
    <>
      <ComplianceChecklistPanel obligation={active} report={report} />
      {report && report.unreconciled.length > 0 ? (
        <Panel
          title="Differences nobody has explained"
          sub="Each one holds this filing until a person writes down why"
          column="1 / 13"
        >
          <Unreconciled report={report} />
        </Panel>
      ) : null}
      {/* What the company has already disclosed for earlier years, and the reconciliation that
          explains any difference from it, live on their own screen (ADR-0032). Linked from here
          with the filing, so a reader who has just read a 20H check does not have to find it. */}
      <Panel
        title="Earlier years"
        sub="What your company has already disclosed, and why it differs from today's figures"
        column="1 / 13"
      >
        <p className={own.fine}>
          <Link to="/prior-disclosures" search={{ method, filing: active.obligation_id }} data-testid="prior-disclosures-link">
            Open prior disclosures and the reconciliation ›
          </Link>
        </p>
      </Panel>
      {/* **Last, and a question about the filing rather than a step in making one** — REP-08.
          `PackagePreflight.tsx`'s header argues the placement in full, including why it is not on
          Administration: the route is obligation-scoped, and this is the only screen that
          resolves an obligation. */}
      <PackagePreflightPanel
        obligationId={active.obligation_id}
        entityName={active.legal_entity_name}
      />
    </>
  );
}

/** A failing check as the finding row draws it. The row wants a link or nothing, so a check with no
 * page (EmitIQ's own) carries an empty address. */
function findingOf(check: FilingCheck): Finding {
  return {
    key: check.key,
    condition: check.kind,
    severity: check.state === "blocking" ? "blocking" : "advisory",
    message: check.message,
    remedy: check.remedy,
    citation: check.citation,
    source_url: check.source_url ?? "",
    detail: check.detail,
  };
}

/** The Findings view: **the checks that did not pass**, from the same read the filing's page counts.
 *
 * It used to list `report.findings`, which is the regulation's own rules only. The page counted all
 * the filing's checks (a bill that never arrived, an approval nobody has given), so a filing with
 * three blocking checks said "0 blocking, 1 advisory" here under a red "Incomplete" banner
 * (design review 2026-11-03). One read now feeds both, so the numbers cannot differ. */
export function FindingsTab({
  checks,
}: {
  checks: { data?: FilingChecks | undefined; error?: unknown; refetch?: () => unknown };
}) {
  if (checks.error && !checks.data) {
    return (
      <Panel title="Findings" sub="The checks this filing did not pass" column="1 / 13">
        <ReadFailed
          of="This filing's checks"
          error={checks.error}
          onRetry={() => void checks.refetch?.()}
        />
      </Panel>
    );
  }
  if (!checks.data) {
    return (
      <Panel title="Findings" sub="The checks this filing did not pass" column="1 / 13">
        <Empty>The findings come from this filing&rsquo;s checks, which have not been read yet.</Empty>
      </Panel>
    );
  }
  const read = checks.data;
  const failing = failingChecks(read.checks);
  return (
    <Panel title="Findings" sub={countsLine(read)} column="1 / 13">
      {failing.length > 0 ? (
        <div className={cls(own.findings)} data-testid="findings">
          {failing.map((check) => (
            <FindingRow key={check.key} finding={findingOf(check)} />
          ))}
        </div>
      ) : (
        <Empty>
          {read.summary.total === 0
            ? "No checks ran, so there are no findings to list. That is not a pass."
            : "No check has failed. That is not an approval: whether this report may be filed is a person's judgement."}
        </Empty>
      )}
      <details className={own.basisBox}>
        <summary>Basis and limits</summary>
        <p className={own.fine}>
          Blocking and advisory are different instructions. A blocking finding has to be fixed
          before the report passes its checks; an advisory one has to be read (20H excepts a first
          year, so a company with no prior-year figures is advised and not stopped). Every finding
          cites the paragraph it comes from.
        </p>
      </details>
    </Panel>
  );
}
